import { Amplify } from 'aws-amplify';
import {
  getCurrentUser,
  signIn,
  signOut,
  fetchAuthSession,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  confirmSignIn as amplifyConfirmSignIn,
  updatePassword as amplifyUpdatePassword,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchUserAttributes,
} from 'aws-amplify/auth';

/**
 * AWS Amplify Authentication Configuration
 * Cognito User Pool for dashboard authentication.
 * Pool/client ids come from REACT_APP_* env vars (see .env.example); the
 * production ids are the fallback so a plain `npm start` still works.
 */

const COGNITO_CONFIG = {
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID || 'us-east-1_jiD9GvFxm',
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '2icsv32h71264sk3daf5eoa4ml',
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
    },
  },
};

Amplify.configure(COGNITO_CONFIG);

/** Sign-in outcomes the UI knows how to handle. */
export type SignInNextStep =
  | 'DONE'
  | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
  | 'CONFIRM_SIGN_UP'
  | 'RESET_PASSWORD'
  | 'MFA_REQUIRED'
  | 'UNKNOWN';

function mapNextStep(step: string | undefined): SignInNextStep {
  switch (step) {
    case undefined:
    case 'DONE':
      return 'DONE';
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
    case 'CONFIRM_SIGN_UP':
    case 'RESET_PASSWORD':
      return step;
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
      return 'MFA_REQUIRED';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Authentication Service
 * Wrapper around AWS Amplify Auth
 */
class AuthService {
  /**
   * Sign in with email and password.
   * Returns the next step the UI must handle (DONE when fully signed in).
   */
  async login(email: string, password: string): Promise<{ nextStep: SignInNextStep; rawStep?: string }> {
    // Clear any stale sign-in state before attempting
    try { await signOut(); } catch { /* ignore */ }
    const result = await signIn({ username: email, password });
    const rawStep = result.nextStep?.signInStep;
    return { nextStep: result.isSignedIn ? 'DONE' : mapNextStep(rawStep), rawStep };
  }

  /**
   * Complete sign-in challenge (e.g. set new password for FORCE_CHANGE_PASSWORD users)
   */
  async confirmSignIn(newPassword: string): Promise<void> {
    await amplifyConfirmSignIn({ challengeResponse: newPassword });
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await amplifyUpdatePassword({ oldPassword, newPassword });
  }

  /** Start the forgot-password flow (Cognito emails a code). */
  async requestPasswordReset(email: string): Promise<void> {
    await amplifyResetPassword({ username: email });
  }

  /** Finish the forgot-password flow with the emailed code. */
  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    await amplifyConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
  }

  /**
   * Get user profile attributes
   */
  async getUserAttributes(): Promise<Record<string, string>> {
    try {
      const attributes = await fetchUserAttributes();
      const result: Record<string, string> = {};
      for (const [key, value] of Object.entries(attributes)) {
        if (value) result[key] = value;
      }
      return result;
    } catch (error) {
      console.error('Failed to get user attributes:', error);
      return {};
    }
  }

  /**
   * Sign out
   */
  async logout(): Promise<void> {
    await signOut();
  }

  /**
   * Get current authenticated user (null when signed out)
   */
  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  }

  /**
   * Get a current ID token (Amplify refreshes it silently when expired).
   */
  async getAuthToken(forceRefresh = false): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Get orgId from the ID token claims
   */
  async getOrgId(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const orgId = session.tokens?.idToken?.payload?.['custom:orgId'];
      return typeof orgId === 'string' ? orgId : null;
    } catch (error) {
      console.error('Failed to get orgId:', error);
      return null;
    }
  }

  /**
   * Confirm signup with verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    await amplifyConfirmSignUp({ username: email, confirmationCode: code });
  }

  /**
   * Resend signup verification code
   */
  async resendSignUpCode(email: string): Promise<void> {
    await amplifyResendSignUpCode({ username: email });
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return !!user;
  }
}

// Export singleton instance
export const auth = new AuthService();
